/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import React, { memo, Suspense, useMemo } from 'react';

import { css, jsx } from '@compiled/react';
import { ErrorBoundary } from 'react-error-boundary';
import { type IntlShape, useIntl } from 'react-intl';
import { graphql, useLazyLoadQuery } from 'react-relay';

import { cssMap } from '@atlaskit/css';
import { Text } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';
import { logException } from '@atlassian/conversation-assistant-instrumentation/services/sentry';

import { parseGoogleCalendarUrl } from '../../../common/google-calendar/parseGoogleCalendarUrl';
import { GoogleCalendarIcon } from '../../google-calendar-icon/GoogleCalendarIcon';
import messages from '../messages';

import type { GoogleCalendarCardCurrentUserQuery as GoogleCalendarCardCurrentUserQueryType } from './__generated__/GoogleCalendarCardCurrentUserQuery.graphql';
import type { GoogleCalendarCardEventsQuery as GoogleCalendarCardEventsQueryType } from './__generated__/GoogleCalendarCardEventsQuery.graphql';
import type { GoogleCalendarCardTenantContextQuery as GoogleCalendarCardTenantContextQueryType } from './__generated__/GoogleCalendarCardTenantContextQuery.graphql';
import {
	CYPHER_QUERY,
	type FetchedGoogleCalendarEvent,
	getCloudIdFromRuntime,
	resolveTargetTimeMs,
	WINDOW_MS,
} from './googleCalendarEvents';
import { useGoogleCalendarConnector } from './useGoogleCalendarConnector';

const containerStyles = css({
	display: 'flex',
	flexDirection: 'column',
	gap: token('space.050'),
	borderStyle: 'solid',
	borderWidth: token('border.width'),
	borderColor: token('color.border'),
	borderRadius: token('radius.large'),
	width: '100%',
	maxWidth: '100%',
	minWidth: 0,
	boxSizing: 'border-box',
});

const containerVariantStyles = cssMap({
	default: {},
	detail: {
		borderRadius: token('radius.xlarge'),
	},
});

const titleStyles = css({
	display: 'block',
	minWidth: 0,
	// Tighten gap to subtitle by nudging title up 2px (avoids overriding lineHeight).
	position: 'relative',
	bottom: token('space.025'),
	'&:hover': {
		textDecoration: 'underline',
	},
});
const blockStyles = css({
	display: 'flex',
	minWidth: 0,
});
const blockAlignCenterStyles = css({
	alignItems: 'center',
});
const groupColumnStyles = css({
	display: 'flex',
	flexDirection: 'column',
	minWidth: 0,
	flex: 1,
});
const metadataStyles = css({
	display: 'block',
	minWidth: 0,
	overflow: 'hidden',
	whiteSpace: 'nowrap',
	textOverflow: 'ellipsis',
	font: token('font.body.small'),
	color: token('color.text.subtlest'),
});

const renderDateSubtitle = (eventDate: string | undefined): React.ReactElement | null =>
	eventDate ? (
		<span data-smart-element="EventDate" data-smart-element-text="true" css={metadataStyles}>
			{eventDate}
		</span>
	) : null;

export type GoogleCalendarCardProps = {
	url: string;
	/** Suppresses the provider subtitle row (used by the compact group layout). */
	hideSubtitle?: boolean;
	/** Uses the title style that matches the detail artifact card list. */
	titleAppearance?: 'default' | 'detail';
};

const formatEventDate = (
	formatDate: IntlShape['formatDate'],
	startIso?: string,
): string | undefined => {
	if (!startIso) return undefined;
	const startMs = Date.parse(startIso);
	if (Number.isNaN(startMs)) return undefined;
	const startDate = new Date(startMs);

	return formatDate(startDate, {
		month: 'short',
		day: 'numeric',
		year: 'numeric',
	});
};

const GoogleCalendarCardLayout = ({
	title,
	subtitle,
	hideSubtitle = false,
	titleAppearance = 'default',
}: {
	title: React.ReactNode;
	subtitle: React.ReactNode;
	/** When `true`, the subtitle row is omitted. */
	hideSubtitle?: boolean;
	titleAppearance?: GoogleCalendarCardProps['titleAppearance'];
}): React.ReactElement => {
	const { formatMessage } = useIntl();
	return (
		<div
			data-smart-link-container="true"
			css={[containerStyles, containerVariantStyles[titleAppearance]]}
		>
			<div data-smart-block="true" css={[blockStyles, !hideSubtitle && blockAlignCenterStyles]}>
				<div data-smart-element-icon="true">
					<GoogleCalendarIcon label={formatMessage(messages.googleCalendarProvider)} />
				</div>
				<div data-smart-element-group="true" css={groupColumnStyles}>
					<span data-smart-element="Title" css={titleStyles}>
						{titleAppearance === 'detail' ? (
							<Text size="small" maxLines={1} weight="bold">
								{title}
							</Text>
						) : (
							<Text size="small" maxLines={1} weight="medium">
								{title}
							</Text>
						)}
					</span>
					{hideSubtitle ? null : subtitle}
				</div>
			</div>
		</div>
	);
};

// URL-only render: title = URL, snippet = parsed start/end if any.
const FallbackVariant = ({
	url,
	hideSubtitle,
	titleAppearance,
}: GoogleCalendarCardProps): React.ReactElement => {
	const { formatDate } = useIntl();
	const parsed = parseGoogleCalendarUrl(url);
	const eventDate = formatEventDate(formatDate, parsed?.startTimeIso);

	return (
		<GoogleCalendarCardLayout
			title={url}
			subtitle={renderDateSubtitle(eventDate)}
			hideSubtitle={hideSubtitle}
			titleAppearance={titleAppearance}
		/>
	);
};

// Suspense reader: matches the user's calendar events against `url`; falls back to URL-only.
const FetchedVariant = ({
	url,
	hideSubtitle,
	titleAppearance,
	userAri,
	queryContext,
}: GoogleCalendarCardProps & {
	userAri: string;
	queryContext: string;
}): React.ReactElement => {
	const { formatDate } = useIntl();
	const targetTimeIso = parseGoogleCalendarUrl(url)?.startTimeIso;
	const { startMin, startMax } = useMemo(() => {
		const targetMs = resolveTargetTimeMs(targetTimeIso);
		return {
			startMin: new Date(targetMs - WINDOW_MS).toISOString(),
			startMax: new Date(targetMs + WINDOW_MS).toISOString(),
		};
	}, [targetTimeIso]);

	const data = useLazyLoadQuery<GoogleCalendarCardEventsQueryType>(
		graphql`
			query GoogleCalendarCardEventsQuery(
				$cypherQuery: String!
				$params: JSON!
				$queryContext: String!
			) {
				graphStore @optIn(to: ["GraphStore", "GraphStoreCypherQueryV2"]) {
					cypherQueryV2(query: $cypherQuery, params: $params, queryContext: $queryContext) {
						edges {
							node {
								columns {
									__typename
									value {
										__typename
										... on GraphStoreCypherQueryV2AriNode {
											data {
												__typename
												... on ExternalCalendarEvent {
													id
													displayName
													eventStartTime
													eventEndTime
													url
												}
											}
										}
									}
								}
							}
						}
					}
				}
			}
		`,
		{
			cypherQuery: CYPHER_QUERY,
			params: { me: userAri, startMin, startMax },
			queryContext,
		},
		{
			fetchPolicy: 'store-or-network',
			networkCacheConfig: {
				metadata: {
					headers: {
						'X-Force-Flock': 'false',
						'X-Force-Dynamo': 'true',
					},
				},
			},
		},
	);
	// Inline the mapping so each Relay-tracked field is consumed in this file
	// (satisfies @atlassian/relay/unused-fields, which only inspects the file
	// containing the graphql document).
	const events: FetchedGoogleCalendarEvent[] = (data?.graphStore?.cypherQueryV2?.edges ?? [])
		.flatMap((edge) => edge?.node?.columns ?? [])
		.map((column): FetchedGoogleCalendarEvent | null => {
			if (!column || column.value?.__typename !== 'GraphStoreCypherQueryV2AriNode') {
				return null;
			}
			const entity = column.value.data;
			if (entity?.__typename !== 'ExternalCalendarEvent' || !entity.id) {
				return null;
			}
			return {
				id: entity.id,
				url: entity.url ?? null,
				title: entity.displayName ?? '',
				startTimeIso: entity.eventStartTime ?? null,
				endTimeIso: entity.eventEndTime ?? null,
			};
		})
		.filter((event): event is FetchedGoogleCalendarEvent => event !== null);
	const parsed = parseGoogleCalendarUrl(url);
	// Match by series id (prefix before `_<timestamp>`) to cover recurring/cross-attendee shares.
	const seriesIdOf = (eventId: string | undefined | null): string | null =>
		eventId ? (eventId.split('_')[0] ?? null) : null;
	const targetSeriesId = seriesIdOf(parsed?.eventId);
	const matched = targetSeriesId
		? events.find((event) => {
				const candidateSeriesId = event.url
					? seriesIdOf(parseGoogleCalendarUrl(event.url)?.eventId)
					: null;
				return candidateSeriesId === targetSeriesId;
			})
		: undefined;

	if (!matched) {
		return (
			<FallbackVariant url={url} hideSubtitle={hideSubtitle} titleAppearance={titleAppearance} />
		);
	}

	const eventDate = formatEventDate(formatDate, matched.startTimeIso ?? undefined);

	return (
		<GoogleCalendarCardLayout
			title={matched.title || url}
			subtitle={renderDateSubtitle(eventDate)}
			hideSubtitle={hideSubtitle}
			titleAppearance={titleAppearance}
		/>
	);
};

const FetchedVariantHost = ({
	url,
	hideSubtitle,
	titleAppearance,
}: GoogleCalendarCardProps): React.ReactElement => {
	const currentUser = useLazyLoadQuery<GoogleCalendarCardCurrentUserQueryType>(
		graphql`
			query GoogleCalendarCardCurrentUserQuery {
				me {
					user {
						accountId
					}
				}
			}
		`,
		{},
		{ fetchPolicy: 'store-or-network' },
	);
	const accountId = currentUser?.me?.user?.accountId ?? null;
	const userAri = accountId ? `ari:cloud:identity::user/${accountId}` : null;

	const cloudId = getCloudIdFromRuntime();
	const tenantContext = useLazyLoadQuery<GoogleCalendarCardTenantContextQueryType>(
		graphql`
			query GoogleCalendarCardTenantContextQuery($cloudIds: [ID!]!) {
				tenantContexts(cloudIds: $cloudIds) {
					orgId
				}
			}
		`,
		{ cloudIds: cloudId ? [cloudId] : [] },
		{ fetchPolicy: 'store-or-network' },
	);
	const orgId = tenantContext?.tenantContexts?.[0]?.orgId ?? null;
	const queryContext = orgId ? `ari:cloud:platform::org/${orgId}` : null;

	if (!userAri || !queryContext) {
		return (
			<FallbackVariant url={url} hideSubtitle={hideSubtitle} titleAppearance={titleAppearance} />
		);
	}
	return (
		<FetchedVariant
			url={url}
			hideSubtitle={hideSubtitle}
			titleAppearance={titleAppearance}
			userAri={userAri}
			queryContext={queryContext}
		/>
	);
};

const GoogleCalendarCardBase = ({
	url,
	hideSubtitle,
	titleAppearance,
}: GoogleCalendarCardProps): React.ReactElement => {
	const connector: ReturnType<typeof useGoogleCalendarConnector> = useGoogleCalendarConnector();
	const isConnected = Boolean(connector?.isUserOAuthed);

	if (!isConnected) {
		return (
			<FallbackVariant url={url} hideSubtitle={hideSubtitle} titleAppearance={titleAppearance} />
		);
	}

	const fallback = (
		<FallbackVariant url={url} hideSubtitle={hideSubtitle} titleAppearance={titleAppearance} />
	);

	// Degrade to URL-only on both pending and error states. Errors are
	// reported to Sentry so the silent fallback stays observable.
	return (
		<ErrorBoundary
			fallback={fallback}
			resetKeys={[url]}
			onError={(error) => {
				logException({ exception: error, name: 'googleCalendarCardRelay' });
			}}
		>
			<Suspense fallback={fallback}>
				<FetchedVariantHost
					url={url}
					hideSubtitle={hideSubtitle}
					titleAppearance={titleAppearance}
				/>
			</Suspense>
		</ErrorBoundary>
	);
};

export const GoogleCalendarCard: React.MemoExoticComponent<typeof GoogleCalendarCardBase> =
	memo(GoogleCalendarCardBase);
